import { useState, useEffect, useRef, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d9ki4ighpes2mi34sn2g';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const productos = [
  'ALPHA-29 - Demulsificante',
  'ANTIESPUMANTE ALPHAS-28 para Crudo',
  'Secuestrante de Oxígeno ALPHA-42',
  'Nivelador de pH ALPHA',
  'Biocida ALPHA-46',
  'Inhibidor de Corrosión ALPHA-43',
  'Inhibidor de Incrustación ALPHA-44',
  'Clarificante de Agua ALPHA-41',
  'ALFA INHIBIT®',
  'POLY ALPHAS 7®',
  'EZP-480R®',
  'Otro / Consulta General',
];

const highlights = [
  {
    icon: 'ri-timer-flash-line',
    title: 'Respuesta en 24 horas',
    desc: 'Nuestro equipo técnico evalúa tu solicitud y te envía una cotización personalizada en menos de un día hábil.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Asesoría técnica gratuita',
    desc: 'Te ayudamos a seleccionar el producto químico ideal para tu operación sin costo adicional.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Entrega a nivel nacional',
    desc: 'Coordinamos logística y envío a cualquier punto de Venezuela con seguimiento en tiempo real.',
  },
];

export default function QuickQuote() {
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [formError, setFormError] = useState('');
  const sectionRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      formRef.current?.reset();
      return;
    }

    setFormStatus('sending');
    setFormError('');

    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((val, key) => {
      if (key !== 'phone_alt') params.append(key, val.toString());
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        formRef.current?.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || 'Error al enviar. Intenta de nuevo.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Error de conexión. Por favor intenta de nuevo.');
    }
  };

  return (
    <section ref={sectionRef} className="w-full py-20 md:py-28 bg-neutral-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-8 h-px bg-primary-500" />
            <span className="text-primary-400 text-xs font-semibold tracking-widest uppercase">Sin Compromiso</span>
            <div className="w-8 h-px bg-primary-500" />
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl text-white leading-tight">
            Cotización <span className="text-primary-500">Rápida</span>
          </h2>
          <p className="mt-3 text-neutral-400 text-base max-w-lg mx-auto">
            Completa el formulario y recibe tu cotización personalizada en menos de 24 horas.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">

          {/* Form - 3 columns */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            {formStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 px-8 text-center bg-neutral-900 border border-neutral-800 rounded-2xl">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/20 mb-5">
                  <i className="ri-check-line text-primary-400 text-3xl"></i>
                </div>
                <h3 className="font-heading font-bold text-2xl text-white mb-2">¡Solicitud Enviada!</h3>
                <p className="text-neutral-400 text-sm max-w-sm mb-6">
                  Hemos recibido tu solicitud de cotización. Nuestro equipo técnico se comunicará contigo en las próximas 24 horas.
                </p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Solicitar otra cotización
                </button>
              </div>
            ) : (
              <form
                id="d9ki4ighpes2mi34sn2g"
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 md:p-8"
              >
                {/* Step indicator */}
                <div className="flex items-center gap-3 mb-7">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold">1</span>
                  <span className="text-neutral-200 text-sm font-semibold">Datos de contacto</span>
                  <div className="flex-1 h-px bg-neutral-800"></div>
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral-800 text-neutral-500 text-xs font-bold">2</span>
                  <span className="text-neutral-500 text-sm font-medium">Detalles del producto</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-neutral-300 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      required
                      placeholder="Ingresa tu nombre completo"
                      className="w-full px-4 py-3 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-neutral-300 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Empresa
                    </label>
                    <input
                      type="text"
                      name="empresa"
                      placeholder="Nombre de tu empresa"
                      className="w-full px-4 py-3 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-neutral-300 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Correo Electrónico *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="tu@correo.com"
                      className="w-full px-4 py-3 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-neutral-300 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Teléfono / WhatsApp *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      required
                      placeholder="+58 412 000 0000"
                      className="w-full px-4 py-3 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    />
                  </div>
                </div>

                {/* Step 2 divider */}
                <div className="flex items-center gap-3 my-7">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral-800 text-neutral-500 text-xs font-bold">1</span>
                  <span className="text-neutral-500 text-sm font-medium">Datos de contacto</span>
                  <div className="flex-1 h-px bg-primary-600"></div>
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold">2</span>
                  <span className="text-neutral-200 text-sm font-semibold">Detalles del producto</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-neutral-300 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Producto de Interés *
                    </label>
                    <select
                      name="producto"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all cursor-pointer appearance-none"
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23a3a3a3' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '16px 12px' }}
                    >
                      <option value="" className="bg-neutral-900 text-neutral-500">Selecciona un producto</option>
                      {productos.map((p) => (
                        <option key={p} value={p} className="bg-neutral-900 text-white">{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-neutral-300 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                      Cantidad Estimada
                    </label>
                    <input
                      type="text"
                      name="cantidad"
                      placeholder="Ej: 200 litros, 5 tambores..."
                      className="w-full px-4 py-3 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-neutral-300 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                    Descripción de tu necesidad *
                  </label>
                  <textarea
                    name="mensaje"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Describe brevemente tu aplicación, volumen requerido, frecuencia de compra, o cualquier detalle relevante para tu cotización..."
                    className="w-full px-4 py-3 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all resize-none"
                  />
                  <p className="text-neutral-500 text-xs mt-1">Máximo 500 caracteres</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                {formStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 mb-4">
                    <i className="ri-error-warning-line text-red-400 text-sm"></i>
                    <span className="text-red-300 text-sm">{formError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full py-3.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {formStatus === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando solicitud...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Cotizar Productos
                    </>
                  )}
                </button>

                <p className="text-neutral-500 text-xs text-center mt-4">
                  Tus datos están seguros. No compartimos información con terceros.
                </p>
              </form>
            )}
          </div>

          {/* Side info - 2 columns */}
          <div className={`lg:col-span-2 flex flex-col gap-5 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            {/* Highlights */}
            <div className="space-y-4">
              {highlights.map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 p-5 rounded-xl bg-neutral-900 border border-neutral-800 transition-all duration-200 hover:border-primary-500/50"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/20 flex-shrink-0">
                    <i className={`${item.icon} text-primary-400 text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white text-sm mb-1">{item.title}</h4>
                    <p className="text-neutral-400 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick contact card */}
            <div className="p-5 rounded-xl bg-primary-500 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/20">
                  <i className="ri-whatsapp-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-sm font-bold">¿Prefieres WhatsApp?</p>
                  <p className="text-primary-100 text-xs">Respuesta inmediata</p>
                </div>
              </div>
              <a
                href="https://wa.me/584128334239"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-white text-primary-600 hover:bg-primary-50 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                +58 412 833 4239
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}